import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { useNavigate, useParams } from "react-router-dom";
import { getLoggedInUser } from "../../helpers/backend_helper";
import { getData, postData } from "../../helpers/url-request";
import {
  BASE_URL,
  DOWNLOAD_IMPORT_DEFINITION_TEMPLATE,
  GET_IMPORT_DEFINITION_LIST,
  SAVE_IMPORT_DEFINITION,
} from "../../helpers/url_helper";
import { getFormatLabel, getSourceSystem } from "./importDefinitionConfig";
import "./ImportDefinitionView.scss";

const fieldsByDestination = {
  Clients: [
    "Client Code",
    "Client Name",
    "Client Type",
    "Email",
    "Phone Number",
    "Industry",
    "External Client ID",
  ],
  Engagements: [
    "Engagement Code",
    "Engagement Name",
    "Work Type",
    "Client Code",
    "Client Name",
    "Client Type",
    "Industry",
    "Description",
    "Financial Year End",
    "Period End Date",
    "Engagement Partner",
    "Manager",
    "External Engagement ID",
  ],
  Contacts: ["Client Code", "Contact Name", "Email", "Phone Number", "Role"],
  Users: ["User Name", "Email", "First Name", "Last Name", "Role"],
};

const normalise = (value) =>
  String(value || "")
    .replace(/[^a-z0-9]/gi, "")
    .toLowerCase();

const isRequired = (column) =>
  new Set([
    "clientid",
    "clientcode",
    "clientname",
    "engno",
    "engagementid",
    "engagementcode",
    "engdesc",
    "engagementname",
    "engagementtype",
    "worktype",
    "financialyearend",
    "finyearend",
    "yearend",
    "periodend",
    "periodenddate",
    "primarycontactemail",
  ]).has(normalise(column));

const suggestedField = (column, fields) => {
  const aliases = {
    clientid: "Client Code",
    clientcode: "Client Code",
    clientname: "Client Name",
    clienttype: "Client Type",
    industry: "Industry",
    industrytype: "Industry",
    description: "Description",
    engno: "Engagement Code",
    engagementid: "Engagement Code",
    engdesc: "Engagement Name",
    engagementdescription: "Description",
    engagementtype: "Work Type",
    worktype: "Work Type",
    financialyearend: "Financial Year End",
    finyearend: "Financial Year End",
    yearend: "Financial Year End",
    periodend: "Period End Date",
    partner: "Engagement Partner",
  };
  const source = normalise(column);
  return (
    fields.find((field) => normalise(field) === source) || aliases[source] || ""
  );
};

const formatAuditDate = (value) => {
  if (!value) return "Not available";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not available";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const auditUserName = (definition, field) => {
  const displayName = definition?.[`${field}Name`];
  if (displayName) return displayName;
  return definition?.sourceSystem === "PIERS"
    ? "PIERS System"
    : "Not available";
};

const ImportDefinitionViewPage = ({
  definition: definitionProp = null,
  definitionId,
  onClose,
  onEdit,
  onDuplicate,
  modal = false,
}) => {
  const { id: routeDefinitionId } = useParams();
  const id = definitionId || definitionProp?.id || routeDefinitionId;
  const navigate = useNavigate();
  const [definition, setDefinition] = useState(definitionProp);
  const [structure, setStructure] = useState(() => definition?.structure || {});
  const [mappings, setMappings] = useState(() => definition?.mappings || []);
  const [loadError, setLoadError] = useState("");
  const [downloadProgress, setDownloadProgress] = useState(null);
  const [showAllMappings, setShowAllMappings] = useState(false);
  const source = getSourceSystem(definition?.sourceSystem);
  const format = getFormatLabel(
    definition?.sourceSystem,
    definition?.formatType
  );
  const destinationFields = fieldsByDestination[definition?.target] || [];
  const mappedCount =
    mappings.filter((mapping) => mapping.destination).length ||
    Number(definition?.mapped || 0);
  const columnCount = mappings.length || Number(definition?.columns || 0);
  const visibleMappings = showAllMappings ? mappings : mappings.slice(0, 5);
  const rules = definition?.rules || {};

  useEffect(() => {
    setDefinition(definitionProp || null);
    setLoadError("");
    setShowAllMappings(false);
  }, [definitionProp, id]);

  useEffect(() => {
    if (!id) {
      setLoadError("Import definition not found.");
      return undefined;
    }
    let isCurrent = true;
    getData(`${GET_IMPORT_DEFINITION_LIST}/${id}`)
      .then((response) => {
        if (isCurrent && response?.status) setDefinition(response.data);
        else if (isCurrent)
          setLoadError(response?.message || "Import definition not found.");
      })
      .catch(() => {
        if (isCurrent) setLoadError("Import definition could not be loaded.");
      });
    return () => {
      isCurrent = false;
    };
  }, [id]);

  useEffect(() => {
    if (!definition) return;
    setStructure(definition.structure || {});
    setMappings(definition.mappings || []);
  }, [definition]);

  useEffect(() => {
    const blobName = definition?.templateStorage?.blobName;
    const templateUrl = definition?.templateUrl;
    if (!definition || (!blobName && !templateUrl)) return undefined;
    let isCurrent = true;

    const loadTemplateDetails = async () => {
      try {
        const response = await fetch(
          templateUrl ||
            `${BASE_URL}${DOWNLOAD_IMPORT_DEFINITION_TEMPLATE}?blobName=${encodeURIComponent(
              blobName
            )}`,
          {
            credentials: "include",
          }
        );
        if (!response.ok)
          throw new Error("The saved template could not be loaded.");
        const workbook = XLSX.read(
          await (await response.blob()).arrayBuffer(),
          { type: "array" }
        );
        const worksheet = workbook.SheetNames[0];
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[worksheet], {
          header: 1,
          defval: "",
        });
        const headers = (data[0] || [])
          .map((header) => String(header).trim())
          .filter(Boolean);
        if (!headers.length)
          throw new Error("The saved template does not contain a header row.");
        if (!isCurrent) return;
        setStructure((current) => ({
          ...current,
          sheets: workbook.SheetNames,
          worksheet: current.worksheet || worksheet,
          headers,
          preview: data.slice(1, 6),
          totalRows: Math.max(data.length - 1, 0),
        }));
        if (!definition.mappings?.length)
          setMappings(
            headers.map((column, index) => ({
              source: column,
              sample: String(data[1]?.[index] || "—"),
              destination: suggestedField(column, destinationFields),
              required: isRequired(column),
            }))
          );
      } catch (error) {
        if (isCurrent)
          setLoadError(
            error.message || "The saved template could not be loaded."
          );
      }
    };
    loadTemplateDetails();
    return () => {
      isCurrent = false;
    };
  }, [definition, destinationFields]);

  const downloadTemplate = async () => {
    const blobName = definition?.templateStorage?.blobName;
    const url =
      definition?.templateUrl ||
      (blobName &&
        `${BASE_URL}${DOWNLOAD_IMPORT_DEFINITION_TEMPLATE}?blobName=${encodeURIComponent(
          blobName
        )}`);
    if (!url || downloadProgress) return;
    setLoadError("");
    setDownloadProgress({ loaded: 0, total: 0 });
    try {
      const response = await fetch(url, { credentials: "include" });
      if (!response.ok)
        throw new Error("The template file could not be downloaded.");
      const total = Number(response.headers.get("content-length")) || 0;
      const reader = response.body?.getReader();
      let fileBlob;
      if (!reader) fileBlob = await response.blob();
      else {
        const chunks = [];
        let loaded = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.length;
          setDownloadProgress({ loaded, total });
        }
        fileBlob = new Blob(chunks, {
          type:
            response.headers.get("content-type") || "application/octet-stream",
        });
      }
      const objectUrl = URL.createObjectURL(fileBlob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download =
        definition?.templateStorage?.fileName ||
        definition?.file ||
        "import-template.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    } catch (error) {
      setLoadError(
        error.message || "The template file could not be downloaded."
      );
    } finally {
      setDownloadProgress(null);
    }
  };

  const duplicateDefinition = async () => {
    const response = await postData(SAVE_IMPORT_DEFINITION, {
      ...definition,
      id: null,
      definitionKey: null,
      name: `${definition.name} Copy`,
      status: "Draft",
      templateStorage: null,
    });
    if (response?.status) {
      if (onDuplicate) onDuplicate(response.data);
      else
        navigate(
          `/system/templates/import-definitions/${response.data.id}/edit`
        );
    } else
      setLoadError(
        response?.message || "Import definition could not be duplicated."
      );
  };

  const closeDefinitionView = () => {
    if (onClose) onClose();
    else navigate("/system/templates?tab=import-definitions");
  };

  const editDefinition = () => {
    if (onEdit) onEdit(definition);
    else navigate(`/system/templates/import-definitions/${definition.id}/edit`);
  };

  if (!definition)
    return (
      <main
        className={(`page-content import-definition-view${
          modal ? " import-definition-view--modal" : ""
        }`) + " piers-page"}
      >
        <section className="import-definition-view__empty piers-page-header">
          <h1 className="piers-page-title">{loadError || "Loading import definition…"}</h1>
          <button type="button" onClick={closeDefinitionView}>
            {modal ? "Close" : "Back to Import Definitions"}
          </button>
        </section>
      </main>
    );

  const metadata = [
    ["bx-folder", "Import To", definition.target],
    ["bx-layer", "Source Type", source.label],
    ["bx-spreadsheet", "Format", format],
    ["bx-check-circle", "Mappings", `${mappedCount} / ${columnCount || "—"}`],
  ];
  const overview = [
    ["Import Data To", definition.target],
    ["Source Type", source.label],
    ["Format", format],
    ["Status", definition.status || "Draft"],
    ["Created On", formatAuditDate(definition.createdOn)],
    ["Created By", auditUserName(definition, "createdBy")],
  ];

  return (
    <main
      className={(`page-content import-definition-view${
        modal ? " import-definition-view--modal" : ""
      }`) + " piers-page"}
    >
      <section className="import-definition-view__header">
        <div className="import-definition-view__heading">
          <div className="import-definition-view__title-group">
            {!modal && (
              <button
                className="import-definition-view__back"
                type="button"
                onClick={closeDefinitionView}
              >
                <i className="bx bx-left-arrow-alt" /> Import Definitions
              </button>
            )}
            <div className="piers-page-header">
              <h1 className="piers-page-title">{definition.name}</h1>
              <span
                className={`import-definition-view__status is-${String(
                  definition.status || "draft"
                ).toLowerCase()}`}
              >
                {definition.status || "Draft"}
              </span>
              {definition.sourceSystem === "PIERS" && (
                <span className="import-definition-view__system">
                  PIERS Standard (System)
                </span>
              )}
              <p>{definition.description || "No description provided."}</p>
            </div>
          </div>
          <div className="import-definition-view__actions">
            <button
              aria-live="polite"
              disabled={Boolean(downloadProgress)}
              type="button"
              onClick={downloadTemplate}
            >
              <i
                className={`bx ${
                  downloadProgress ? "bx-loader-alt bx-spin" : "bx-download"
                }`}
              />{" "}
              {downloadProgress
                ? downloadProgress.total
                  ? `Downloading ${Math.min(
                      100,
                      Math.round(
                        (downloadProgress.loaded / downloadProgress.total) * 100
                      )
                    )}%`
                  : "Preparing download…"
                : "Download Template"}
            </button>
            {!modal && (
              <>
                <button type="button" onClick={duplicateDefinition}>
                  <i className="bx bx-copy" /> Duplicate
                </button>
                <button
                  className="is-primary"
                  type="button"
                  onClick={editDefinition}
                >
                  <i className="bx bx-edit-alt" /> Edit
                </button>
              </>
            )}
            {modal && (
              <button
                className="import-definition-view__close"
                type="button"
                onClick={closeDefinitionView}
              >
                <i className="bx bx-x" /> Close
              </button>
            )}
          </div>
        </div>
        <div className="import-definition-view__metadata">
          {metadata.map(([icon, label, value]) => (
            <div key={label}>
              <i className={`bx ${icon}`} />
              <span>
                {label}: <b>{value}</b>
              </span>
            </div>
          ))}
          <div className="import-definition-view__metadata-right">
            <span>
              <i className="bx bx-calendar" /> Last Updated:{" "}
              <b>
                {formatAuditDate(definition.updatedOn || definition.createdOn)}
              </b>
            </span>
            <span>
              <i className="bx bx-user" /> Updated By:{" "}
              <b>{auditUserName(definition, "updatedBy")}</b>
            </span>
          </div>
        </div>
      </section>
      <section className="import-definition-view__content">
        <section className="import-definition-view__overview">
          <h2>Overview</h2>
          <div>
            {overview.map(([label, value]) => (
              <dl key={label}>
                <dt>{label}</dt>
                <dd
                  className={
                    label === "Status"
                      ? `is-${String(value).toLowerCase()}`
                      : ""
                  }
                >
                  {value}
                </dd>
              </dl>
            ))}
          </div>
        </section>
        {loadError && (
          <aside className="import-definition-view__error">
            <i className="bx bx-error-circle" />
            {loadError}
          </aside>
        )}
        <section className="import-definition-view__grid">
          <div className="import-definition-view__left">
            <section className="import-definition-view__card import-definition-view__structure">
              <header className="piers-page-header">
                <span>
                  <i className="bx bx-folder-open" />
                </span>
                <div>
                  <h2 className="piers-page-title">File Structure</h2>
                  <p>Details about the expected file and layout.</p>
                </div>
                <i className="bx bx-spreadsheet" />
              </header>
              <div className="import-definition-view__structure-file">
                <b>Workbook / Sheet</b>
                <span>
                  {definition.file ||
                    definition.templateStorage?.fileName ||
                    "Saved template"}
                  <i className="bx bx-right-arrow-alt" />
                  {structure.worksheet || "Loading…"}
                </span>
              </div>
              <div className="import-definition-view__structure-details">
                <div>
                  <b>Header Row</b>
                  <span>Row {structure.headerRow || 1}</span>
                </div>
                <div>
                  <b>Data Starts</b>
                  <span>Row {structure.dataStartRow || 2}</span>
                </div>
                <div>
                  <b>Empty Rows</b>
                  <span>
                    {structure.ignoreEmptyRows === false ? "Include" : "Ignore"}
                  </span>
                </div>
              </div>
              <footer>
                <div>
                  <b>Expected Columns</b>
                  <span>
                    {columnCount || structure.headers?.length || 0} columns
                    expected
                  </span>
                </div>
              </footer>
            </section>
            <section className="import-definition-view__card import-definition-view__rules">
              <header>
                <span>
                  <i className="bx bx-slider-alt" />
                </span>
                <div>
                  <h2>Import Rules</h2>
                  <p>Rules that will be applied during import.</p>
                </div>
              </header>
              <div>
                {[
                  [
                    "bx-refresh",
                    "Existing records",
                    rules.existingAction || "Update matching records",
                  ],
                  [
                    "bx-error",
                    "Invalid rows",
                    rules.invalidAction || "Skip row and report error",
                  ],
                  [
                    "bx-link-alt",
                    "Match records by",
                    rules.matchField || "Not configured",
                  ],
                  [
                    "bx-copy",
                    "Duplicate rows",
                    rules.duplicateAction || "Flag as validation error",
                  ],
                  [
                    "bx-minus",
                    "Blank values",
                    rules.preserveBlanks === false
                      ? "Overwrite existing values"
                      : "Do not overwrite existing value",
                  ],
                  [
                    "bx-calendar",
                    "Date format",
                    rules.dateFormat || "Use source format",
                  ],
                ].map(([icon, title, detail]) => (
                  <article key={title}>
                    <span>
                      <i className={`bx ${icon}`} />
                    </span>
                    <div>
                      <b>{title}</b>
                      <p>{detail}</p>
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </div>
          <div className="import-definition-view__right">
            <section
              className="import-definition-view__card import-definition-view__mappings"
              id="import-definition-mappings"
            >
              <header>
                <span>
                  <i className="bx bx-git-merge" />
                </span>
                <div>
                  <h2>Field Mapping</h2>
                  <p>Mapping between source columns and PIERS fields.</p>
                </div>
                <b>
                  {mappedCount} of {columnCount || 0} mapped
                  {columnCount
                    ? ` (${Math.round((mappedCount / columnCount) * 100)}%)`
                    : ""}
                </b>
              </header>
              <div className="import-definition-view__mapping-table">
                <div className="import-definition-view__mapping-heading">
                  <span>Source Column</span>
                  <span>PIERS Field</span>
                  <span>Type</span>
                  <span>Required</span>
                  <span>Transformation / Rule</span>
                </div>
                {visibleMappings.length ? (
                  visibleMappings.map((mapping) => (
                    <div key={mapping.source}>
                      <span>
                        <b>{mapping.source}</b>
                        <i className="bx bx-right-arrow-alt" />
                      </span>
                      <span>{mapping.destination || "Ignored"}</span>
                      <span>
                        {/date|period/i.test(mapping.destination)
                          ? "Date"
                          : "Text"}
                      </span>
                      <span className={mapping.required ? "is-required" : ""}>
                        {mapping.required ? "Yes" : "No"}
                      </span>
                      <span>{mapping.transformation || "—"}</span>
                    </div>
                  ))
                ) : (
                  <p>Loading saved field mappings…</p>
                )}
              </div>
              {mappings.length > 5 && (
                <footer>
                  <button
                    type="button"
                    onClick={() => setShowAllMappings((current) => !current)}
                  >
                    {showAllMappings
                      ? "Show fewer mappings"
                      : `View all ${mappings.length} mappings`}{" "}
                    <i
                      className={`bx ${
                        showAllMappings ? "bx-chevron-up" : "bx-chevron-right"
                      }`}
                    />
                  </button>
                </footer>
              )}
            </section>
            {definition.sourceSystem === "PIERS" && (
              <aside className="import-definition-view__managed">
                <i className="bx bx-info-circle" />
                <div>
                  <b>PIERS-managed definition</b>
                  <span>
                    This is a PIERS system-provided import definition. To modify
                    the structure or mappings, create a customized copy.
                  </span>
                </div>
              </aside>
            )}
          </div>
        </section>
      </section>
    </main>
  );
};

export default ImportDefinitionViewPage;
